<template>
  <div class="w-full">
    <div class="mb-4 px-4">
      <ProductListErrorCard v-if="hasError" @onRetry="retryActualization" />
      <div v-else class="w-full">
        <p class="text-text-neutral-primary text-base font-bold">
          {{ t('ancillaries.flightDelay.form.flightTo') }} →
        </p>
        <FlightDelaySegments class="pt-4" :flights="flights" />
      </div>
    </div>
    <FlightDelayFooter
      :total-amount="totalAmount"
      :button-text="buttonText"
      :is-actualizing="isActualizing"
      :is-error="hasError"
      @onSave="saveProduct"
      @onDelete="deleteProduct"
    />
  </div>
</template>

<script lang="ts" setup>
  import { computed, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useI18n } from 'vue-i18n';
  import { prettyPrice } from '@aviata/text-fns';
  import { controllerModel } from 'shared';
  import { ProductListErrorCard, type flightEntity } from 'freedom-shared';

  import { bookingStateModel } from '@/features/booking-state';

  import { useFlightDelayStore } from '../model';
  import { flightDelayConfig } from '../config';
  import FlightDelaySegments from './FlightDelaySegments.vue';
  import FlightDelayFooter from './FlightDelayFooter.vue';

  defineProps<{
    flights: flightEntity.types.MappedFlight[];
  }>();

  const emit = defineEmits<{
    onUpdateInitialState: [];
  }>();

  const { t } = useI18n();
  const bottomSheet = controllerModel.useBottomSheet();
  const modal = controllerModel.useModal();
  const bookingStore = bookingStateModel.useBookingStore();

  const flightDelayStore = useFlightDelayStore();
  const { state, isActualizing, hasError, totalAmount } =
    storeToRefs(flightDelayStore);

  watch(
    () =>
      bottomSheet.isVisible(flightDelayConfig.FLIGHT_DELAY_FORM_BOTTOM_SHEET),
    async isVisible => {
      if (isVisible && !state.value.isInitialActualized) {
        await flightDelayStore.fetchInitialActualization();
      }
    },
    { immediate: true },
  );

  const buttonText = computed(() => {
    if (hasError.value || totalAmount.value === 0) {
      return t('general.save');
    }

    return t('ancillaries.medicalInsurance.addFor', {
      price: prettyPrice(totalAmount.value.toString()),
    });
  });

  const saveProduct = () => {
    flightDelayStore.actualizeProduct(bookings => {
      bookingStore.updateFlightDelayProducts(
        bookings.map((booking, i) => ({
          id: booking.id,
          price: booking.price,
          segmentIdx: booking.segmentIdx,
          ...(i === 0 ? { savedSegments: state.value.segments } : {}),
        })),
      );
      emit('onUpdateInitialState');
      bottomSheet.hide(flightDelayConfig.FLIGHT_DELAY_FORM_BOTTOM_SHEET);
      modal.show(flightDelayConfig.FLIGHT_DELAY_SUCCESS_MODAL);
    });
  };

  const deleteProduct = () => {
    bookingStore.nullifyFlightDelayProducts();
    emit('onUpdateInitialState');
    bottomSheet.hide(flightDelayConfig.FLIGHT_DELAY_FORM_BOTTOM_SHEET);
  };

  const retryActualization = () => {
    flightDelayStore.retryActualization();
  };
</script>
